import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DateUtilService, PageableUtilService } from '@services/util';
import {
  Notification,
  NotificationFilterParams,
  PageableData,
  TableParams,
  UnreadCommentCounts,
} from '@shared/models';
import { Observable } from 'rxjs';
import { format, subMonths } from 'date-fns';
import { DATE_FORMAT } from '@shared/constants';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly API_URL = `/api/notif/notification`;

  constructor(
    private httpClient: HttpClient,
    private dateUtil: DateUtilService,
    private util: PageableUtilService,
  ) {}

  public getNotifications(
    tableParams: TableParams,
    filters?: NotificationFilterParams,
  ): Observable<PageableData<Notification>> {
    // set 3 months filter for notifications
    const now = new Date();
    const fromDate = format(subMonths(now, 3), DATE_FORMAT.ISO_DATE);
    const toDate = format(now, DATE_FORMAT.ISO_DATE);
    const utcDateRange = this.dateUtil.calculateUTCDateRange(fromDate, toDate);
    if (!filters) filters = new NotificationFilterParams();
    filters.createdDateFrom = utcDateRange.startOfDayUTC;
    filters.createdDateTo = utcDateRange.endOfDayUTC;

    const params = this.util.buildParams(tableParams, filters);
    return this.httpClient.get<PageableData<Notification>>(`${this.API_URL}`, {
      params,
    });
  }

  public getUnreadCount(): Observable<{ unreadCount: number }> {
    return this.httpClient.get<{ unreadCount: number }>(`${this.API_URL}/unread-count`);
  }

  public deleteNotification(code: string): Observable<Notification> {
    return this.httpClient.delete<Notification>(`${this.API_URL}/${code}`);
  }

  public bulkDeleteNotification(
    codes: string[] | null,
    all: boolean,
  ): Observable<{ message: string }> {
    const payload = { codes, all };
    if (all) delete payload.codes;
    return this.httpClient.post<{ message: string }>(
      `${this.API_URL}/delete-bulk`,
      payload,
    );
  }

  public readAllNotifications(): Observable<unknown> {
    return this.httpClient.get<unknown>(`${this.API_URL}/read-all`);
  }

  /**
   * Unread comment totals per issue code. Driven by the realtime stream rather
   * than by anything the user did, so it opts out of the global error toast: a
   * background refresh failing is not something to interrupt them with.
   */
  public getUnreadCommentCounts(): Observable<UnreadCommentCounts> {
    const headers = new HttpHeaders().set('skip', 'true');
    return this.httpClient.get<UnreadCommentCounts>(
      `${this.API_URL}/unread-comment-count`,
      { headers },
    );
  }

  /** Marks every comment on an issue read, which is what clears its badge. */
  public readCommentThread(issueCode: string): Observable<unknown> {
    const headers = new HttpHeaders().set('skip', 'true');
    return this.httpClient.post<unknown>(
      `${this.API_URL}/read-thread/${issueCode}`,
      null,
      { headers },
    );
  }
}
